import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, Calendar, FileText, ShieldCheck, ArrowRight, Bell, Bookmark } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS } from "@/lib/content";
import { Overline } from "@/components/Reveal";

const STATUS_COLORS = {
  New: "text-[#D4AF37]", Contacted: "text-blue-400", Qualified: "text-green-400",
  "Call Booked": "text-[#CC5500]", "Active Opportunity": "text-green-400", Closed: "text-neutral-500",
};

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [opps, setOpps] = useState([]);

  useEffect(() => {
    api.get("/dashboard").then(({ data }) => setData(data)).catch(() => {});
    api.get("/opportunities").then(({ data }) => setOpps(data)).catch(() => {});
  }, []);

  const submissions = data?.submissions || [];
  const profileComplete = submissions.length > 0;

  const doLogout = async () => { await logout(); navigate("/"); };

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <header className="border-b border-white/10 bg-[#0A0A0A]/90 backdrop-blur sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="font-display text-xl font-extrabold">MOAGLEE<span className="text-[#CC5500]">.</span></Link>
          <div className="flex items-center gap-4">
            <span className="text-sm text-neutral-400 hidden sm:block">{user?.name}</span>
            <span className="overline text-[#CC5500]">{ROLE_LABELS[user?.role] || user?.role}</span>
            {user?.role === "admin" && <Link to="/admin" className="btn-ghost py-2 px-4 text-sm">Admin</Link>}
            <button onClick={doLogout} className="text-neutral-400 hover:text-white" data-testid="logout-btn"><LogOut size={18} /></button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10">
        <Overline>Command Center</Overline>
        <h1 className="font-display text-3xl sm:text-4xl font-bold mt-2">Welcome back, {user?.name?.split(" ")[0]}.</h1>

        {/* Stat widgets */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
          <Widget icon={ShieldCheck} label="Profile Status" value={profileComplete ? "Active" : "Getting Started"} />
          <Widget icon={FileText} label="Submissions" value={submissions.length} testid="widget-submissions" />
          <Widget icon={Bookmark} label="Network Status" value={user?.network_status === "approved" ? "Member" : "Pending"} />
          <Widget icon={Bell} label="Notifications" value="0" />
        </div>

        <div className="grid lg:grid-cols-3 gap-6 mt-8">
          {/* Submissions */}
          <div className="lg:col-span-2 card-edge p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-display text-xl font-bold">Your Submissions</h2>
              <Link to="/cre" className="text-sm text-[#CC5500] inline-flex items-center gap-1">New intake <ArrowRight size={14} /></Link>
            </div>
            {submissions.length === 0 ? (
              <p className="text-neutral-500 text-sm py-8 text-center">No submissions yet. Choose a path on the homepage to get started.</p>
            ) : (
              <div className="divide-y divide-white/10">
                {submissions.map((s) => (
                  <div key={s.lead_id} className="py-4 flex items-center justify-between" data-testid={`submission-${s.lead_id}`}>
                    <div>
                      <p className="font-medium capitalize">{s.lead_type.replace(/_/g, " ")}</p>
                      <p className="text-neutral-500 text-xs mt-0.5">{s.pipeline} · {new Date(s.created_at).toLocaleDateString()}</p>
                    </div>
                    <span className={`text-sm font-medium ${STATUS_COLORS[s.status] || "text-neutral-400"}`}>{s.status}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Next actions */}
          <div className="space-y-6">
            <div className="card-edge p-6">
              <h2 className="font-display text-xl font-bold mb-4">Recommended Next Actions</h2>
              <div className="space-y-3">
                <Link to="/book-call" className="btn-primary w-full justify-center" data-testid="dash-book-call">
                  <Calendar size={16} /> Book a Strategy Call
                </Link>
                <Link to="/private-network" className="btn-ghost w-full justify-center">Apply to Private Network</Link>
                <Link to="/capital" className="btn-ghost w-full justify-center">Submit a Capital Request</Link>
              </div>
            </div>
          </div>
        </div>

        {/* Opportunities */}
        <div className="mt-10">
          <h2 className="font-display text-2xl font-bold mb-5">Opportunities For You</h2>
          {opps.length === 0 ? (
            <p className="text-neutral-500 text-sm">No opportunities published yet.</p>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {opps.map((o) => (
                <div key={o.opportunity_id} className="card-edge p-6" data-testid={`dash-opp-${o.opportunity_id}`}>
                  <span className="overline text-[#CC5500]">{o.type}</span>
                  <h3 className="font-display text-lg font-bold mt-2">{o.title}</h3>
                  <p className="text-neutral-400 text-sm mt-2">{o.short_description}</p>
                  {o.price_range && <p className="text-white font-semibold mt-3">{o.price_range}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function Widget({ icon: Icon, label, value, testid }) {
  return (
    <div className="card-edge p-5" data-testid={testid}>
      <Icon className="text-[#CC5500]" size={22} strokeWidth={1.5} />
      <p className="overline text-neutral-500 mt-3">{label}</p>
      <p className="font-display text-2xl font-bold mt-1">{value}</p>
    </div>
  );
}
